<template>
  <div class="accordion accordion-bg-white-opacity-80 p-0 m-0 details-legend"
       :class="{'accordion-purple-collapsed': accordion.current === null}"
       id="accordionSimulationInputs">
    <!-- Accordion: Simulation inputs -->
    <div class="accordion-item">
      <h4 class="accordion-header" id="accordionSimulationInputs_Inputs_Heading">
        <button
            class="accordion-button accordion-button-sm py-2"
            type="button"
            data-bs-toggle="collapse"
            data-bs-target="#accordionSimulationInputs_Inputs"
            :class="{
                'collapsed': isAccordionCurrent(constants.ACCORDION.SIMULATION_INPUT) === false,
                'text-secondary disabled': propLoadingExplore === true,
                'text-bg-primary text-white': isAccordionCurrent(constants.ACCORDION.SIMULATION_INPUT) === true,
                'text-primary': isAccordionCurrent(constants.ACCORDION.SIMULATION_INPUT) === false,
            }"
            :aria-expanded="isAccordionCurrent(constants.ACCORDION.SIMULATION_INPUT) ? 'true' : 'false'"
            aria-controls="accordionSimulationInputs_Inputs"
            :disabled="propLoadingExplore === true"
            @click="onClickAccordionToggle(constants.ACCORDION.SIMULATION_INPUT)">
          <i class="bi bi-sliders2-vertical me-2"></i>
          <span>{{ $t('SimulationInputs.SimulationInputs') }}</span>
        </button>
      </h4>
      <div
          id="accordionSimulationInputs_Inputs"
          class="accordion-collapse"
          :class="isAccordionCurrent(constants.ACCORDION.SIMULATION_INPUT) === true ? 'show' : 'collapse'"
          aria-labelledby="accordionSimulationInputs_Inputs_Heading"
          data-bs-parent="#accordionSimulationInputs">
        <div class="accordion-body px-3 pt-2 pb-0 table-small">
          <p class="text-muted text-center lh-1 legend_font_size mb-2">
            {{ $t("Actions.ClickPencil") }}
          </p>
          <table class="table table-bg-white-opacity-0 table-sm">
            <tbody>
            <template v-if="propSimulationInitialConditions !== null">
              <!-- Value: month -->
              <tr>
                <td class="align-middle fw-bold">
                  <i class="bi bi-calendar purple_color me-1"></i>
                  <span>{{ $t('SimulationInputs.Month') }}</span>
                </td>
                <td class="text-end align-middle">
                  <span class="badge rounded-pill text-bg-purple text-capitalize">
                    {{ propSimulationInitialConditions.monthLabel($i18n) }}
                  </span>
                </td>
                <td class="text-end align-middle">
                  <button class="btn btn-sm btn-light" @click="editDate" :disabled="simulationEditDisabled">
                    <i class="bi bi-pencil-square purple_color"></i>
                  </button>
                </td>
              </tr>

              <!-- Value: Snow -->
              <tr>
                <td class="align-middle fw-bold"><i class="bi bi-cloud-snow purple_color me-1"></i>
                  <span>{{ $t('SimulationInputs.Snow') }}</span>
                </td>
                <td class="text-end align-middle">
                  <span class="badge rounded-pill text-bg-purple">
                    <template v-if="simulationMonthIsSummer === true">
                      {{ $t('Units.NotAvailable') }}
                    </template>
                    <template v-else>
                      {{ $t(getPercentileI18NLabel(propSimulationInitialConditions.snowStateQuantile)) }}
                    </template>
                  </span>
                </td>
                <td class="text-end align-middle">
                  <template v-if="simulationMonthIsSummer === false">
                    <button
                        class="btn btn-sm btn-light"
                        @click="editSnowCondition"
                        :disabled="simulationEditDisabled">
                      <i class="bi bi-pencil-square purple_color"></i>
                    </button>
                  </template>
                  <div class="pt-2 pb-1" v-else>
                    &nbsp;
                  </div>
                </td>
              </tr>

              <!-- Value: Soil Water -->
              <tr>
                <td class="align-middle fw-bold">
                  <i class="bi bi-droplet purple_color me-1"></i>
                  <span>{{ $t('SimulationInputs.SoilWater') }}</span>
                </td>
                <td class="text-end align-middle">
                      <span class="badge rounded-pill text-bg-purple">
                        {{ $t(getPercentileI18NLabel(propSimulationInitialConditions.soilWaterQuantile)) }}
                      </span>
                </td>
                <td class="text-end align-middle">
                  <button class="btn btn-sm btn-light d-inline"
                          @click="editSoilWater"
                          :disabled="simulationEditDisabled">
                    <i class="bi bi-pencil-square purple_color"></i>
                  </button>
                </td>
              </tr>

              <!-- Value: Precipitation -->
              <tr v-if="propSimulationInitialConditions.precipitationQuantile !== null">
                <td class="align-middle fw-bold">
                  <i class="bi bi-cloud-drizzle purple_color me-1"></i>
                  <span>{{ $t('SimulationInputs.Precipitation') }}</span>
                </td>
                <td class="text-end align-middle">
                      <span class="badge rounded-pill text-bg-purple">
                        {{ $t(getPercentileI18NLabel(propSimulationInitialConditions.precipitationQuantile)) }}
                      </span>
                </td>
                <td class="text-end align-middle">
                  <button
                      class="btn btn-sm btn-light"
                      @click="editPrecipitation"
                      :disabled="simulationEditDisabled">
                    <i class="bi bi-pencil-square purple_color"></i>
                  </button>
                </td>
              </tr>

              <!-- Value: Temperature -->
              <tr v-if="propSimulationInitialConditions.temperatureQuantile !== null">
                <td class="align-middle fw-bold">
                  <i class="bi bi-thermometer-sun purple_color me-1"></i>
                  <span>{{ $t('SimulationInputs.Temperature') }}</span>
                </td>
                <td class="text-end align-middle">
                      <span class="badge rounded-pill text-bg-purple">
                        {{ $t(getPercentileI18NLabel(propSimulationInitialConditions.temperatureQuantile)) }}
                      </span>
                </td>
                <td class="text-end align-middle">
                  <button
                      class="btn btn-sm btn-light"
                      @click="editTemperature"
                      :disabled="simulationEditDisabled">
                    <i class="bi bi-pencil-square purple_color"></i>
                  </button>
                </td>
              </tr>
            </template>
            <!-- Values: Anthropic works -->
            <tr>
              <td class="align-middle fw-bold"><i class="bi bi-gear-wide-connected purple_color me-1"></i>
                <span>{{ $t('SimulationInputs.AnthropicWorks') }}</span>
              </td>
              <td class="text-end align-middle py-2" colspan="2">
                <span class="badge rounded-pill text-bg-purple">
                  {{ propAnthropicWorksUpdated }}/{{ propAnthropicWorksCount }}
                </span>
                <span class="ms-1 small">{{ $tc('layerInfo.modifiedFemale', propAnthropicWorksUpdated) }}</span>
              </td>
            </tr>
            </tbody>
          </table>

          <!-- Controls -->
          <div class="d-flex justify-content-center my-3">
            <button class="btn btn-success me-2 tooltip-run-simulation"
                    @click="onClickRunSimulation"
                    :data-bs-title="$t('Actions.RunAgain')"
                    data-bs-placement="left"
                    data-bs-html="true"
                    :disabled="propLoadingRunSimulation === true || propBlockRunSimulation === true || propBlockAll === true">
              <div v-if="propLoadingRunSimulation === true" class="spinner-grow spinner-grow-sm me-2" role="status">
                <span class="visually-hidden">Loading...</span>
              </div>
              <i v-else class="bi bi-collection-play-fill me-2"/>
              <span>{{ $t("Actions.RunSimulation") }}</span>
            </button>
            <button class="btn btn-danger" @click="onClickShowAlertInitializeMap"
                    :disabled="propLoadingRunSimulation === true || propBlockAll === true">
              <i class="bi bi-arrow-clockwise me-2"/>
              <span>{{ $t("Actions.Initialize") }}</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Accordion: Simulation results -->
    <div class="accordion-item">
      <h2 class="accordion-header" id="accordionSimulationInputs_Results_Heading">
        <button
            class="accordion-button accordion-button-sm py-2"
            type="button"
            data-bs-toggle="collapse"
            data-bs-target="#accordionSimulationInputs_Results"
            :class="{
              'collapsed': isAccordionCurrent(constants.ACCORDION.SIMULATION_RESULTS) === false,
              'text-secondary disabled': propLoadingRunSimulation === true || propShowTabResults === false,
              'text-bg-primary text-white': isAccordionCurrent(constants.ACCORDION.SIMULATION_RESULTS) === true,
              'text-primary': isAccordionCurrent(constants.ACCORDION.SIMULATION_RESULTS) === false,
            }"
            :aria-expanded="isAccordionCurrent(constants.ACCORDION.SIMULATION_RESULTS) ? 'true' : 'false'"
            aria-controls="accordionSimulationInputs_Results"
            :disabled="propLoadingRunSimulation === true || propShowTabResults === false"
            @click="onClickAccordionToggle(constants.ACCORDION.SIMULATION_RESULTS)">
          <i class="bi bi-globe-europe-africa me-2"></i>
          <span>{{ $t('SimulationInputs.SimulationResults') }}</span>
        </button>
      </h2>
      <div
          id="accordionSimulationInputs_Results"
          class="accordion-collapse"
          :class="isAccordionCurrent(constants.ACCORDION.SIMULATION_RESULTS) === true ? 'show' : 'collapse'"
          aria-labelledby="accordionSimulationInputs_Results_Heading"
          data-bs-parent="#accordionSimulationInputs">
        <div class="accordion-body p-2 px-3">
          <!-- Variable -->
          <div class="row mb-1">
            <div class="col-12">
              <label for="forecast_variable" class="form-label me-2">
                {{ $t('InputSearchFormLabelTitle.Datatype') }}
              </label>
              <i class="bi bi-info-circle tooltip-init cursor-pointer"
                 data-bs-toggle="tooltip"
                 data-bs-html="true"
                 @click="tooltipGlossaryOnClick"
                 :data-bs-title='`<small>${$t(tooltipGlossaryVariableKey())} <span class="fw-semibold">${tooltipGlossaryVariableName()}</span></small>`'></i>
              <select class="form-select form-select-sm" aria-label="Variable" v-model="form.variable"
                      :disabled="propLoadingExplore === true || propBlockAll === true">
                <option disabled value="" selected>{{ $t('formDataType.selectDataType') }}</option>
                <template v-for="(item, key) in propVariables">
                  <option :value="key" v-if="item instanceof VariableConfiguration">
                    {{ item.getNameI18N(this.$i18n.locale) }}
                  </option>
                </template>
              </select>
            </div>
          </div>

          <!-- Month -->
          <div class="row mb-3">
            <div class="col-12">
              <label for="endDatePicker" class="form-label">{{ $t('InputSearchFormLabelTitle.Month') }}</label>
              <MonthPicker :prop-show-title="false"
                           v-model="form.month"
                           :prop-start-month="propSimulationMonth !== null ? propSimulationMonth : 1"
                           :disabled="propLoadingExplore === true || propBlockAll === true"/>
            </div>
          </div>

          <!-- Buttons -->
          <div class="d-flex justify-content-center">
            <button class="btn btn-success"
                    @click="onClickExplore"
                    :disabled="propLoadingExplore === true || simulationResultsEnabled === false || simulationFormComplete === false || propBlockAll === true">
              <div v-if="propLoadingExplore === true" class="spinner-grow spinner-grow-sm me-2" role="status">
                <span class="visually-hidden">Loading...</span>
              </div>
              <i v-else class="bi me-2 bi-compass"/>
              <span>{{ $t('ExploreButtonSearchForm.Explore') }}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Toast: initialize -->
  <Toast
      prop-id="toastInitialize"
      :prop-trigger="showToast"
      prop-border-class="dark"
      :prop-show-close="false"
      :prop-auto-hide="false"
      prop-position="bottom"
      :prop-register-click-outside="true"
      @on-toast-close="toastInitializeClose">
    <!-- Body content of the toast -->
    <template #body>
      <p>{{ $t('tooltips.initializeSimulation') }}</p>
    </template>

    <!-- Action buttons content of the toast -->
    <template #actions>
      <!-- Adding divider using Bootstrap classes -->
      <hr class="my-3 text-black-50">
      <div class="d-flex justify-content-end gap-1">
        <button class="btn btn-danger btn-sm" @click="onClickReloadPage">
          <i class="bi bi-arrow-clockwise me-1"></i>
          <span>{{ $t('Actions.Initialize') }}</span>
        </button>
        <button class="btn btn-secondary btn-sm" @click="toastInitializeClose">
          <i class="bi bi-x-lg me-1"></i>
          <span>{{ $t('Actions.Cancel') }}</span>
        </button>
      </div>
    </template>
  </Toast>

</template>

<script>
import constants from "@/libs/Constants";
import Toast from '@/components/Toast.vue';
import VueDatePicker from '@vuepic/vue-datepicker';
import {SimulationData} from '@/libs/SimulationData';
import {
  disposeTooltipsByClass,
  getPercentileI18NLabel,
  getTooltipsByClass,
  setupTooltipsByClass,
  UUIDfromList
} from "@/libs/Helpers";
import MonthPicker from "@/components/MonthPicker.vue";
import {VariableConfiguration} from "@/libs/VisualizationData";

export default {
  components: {
    MonthPicker,
    VueDatePicker,
    Toast,
  },
  emits: ['onEditInputClick', 'onClickShowInitializeToast', 'onClickAccordionSelect', 'onClickRunSimulation', 'onClickExplore', 'tooltipGlossaryOnClick'],
  props: {
    propSimulationInitialConditions: {
      type: SimulationData,
      default: null
    },
    propLoadingRunSimulation: {
      type: Boolean,
      default: false
    },
    propLoadingExplore: {
      type: Boolean,
      default: false
    },
    propAccordionTriggerInputs: {
      type: Boolean,
      default: false
    },
    propAccordionTriggerResults: {
      type: Boolean,
      default: false
    },
    propAccordionTriggerResultsClearForm: {
      type: Boolean,
      default: false
    },
    propAccordionTriggerHide: {
      type: Boolean,
      default: false
    },
    propBlockAll: {
      type: Boolean,
      default: false
    },
    propBlockRunSimulation: {
      type: Boolean,
      default: false
    },
    propShowTabResults: Boolean,
    propAnthropicWorksUpdated: Number,
    propAnthropicWorksCount: Number,
    propSimulationId: {type: String, default: false},
    propSimulationMonth: {type: Number, default: null},
    propUuidSimulationResults: {type: String, default: null},
    propVariables: Object
  },
  watch: {
    propAccordionTriggerInputs(newValue, oldValue) {
      this.accordionSetCurrent(constants.ACCORDION.SIMULATION_INPUT);
      this.tooltipsShow();
    },
    propAccordionTriggerResults(newValue, oldValue) {
      this.accordionSetCurrent(constants.ACCORDION.SIMULATION_RESULTS);
    },
    propAccordionTriggerResultsClearForm(newValue, oldValue) {
      this.form.month = null;
      this.form.variable = '';
    },
    propAccordionTriggerHide(newValue, oldValue) {
      this.accordionSetCurrent(null);
    },
  },
  data() {
    return {
      constants,
      VariableConfiguration,
      showAboutStartup: false,  // Initialize this to false
      showToast: false,

      accordion: {
        current: constants.ACCORDION.SIMULATION_INPUT
      },

      form: {
        variable: '',
        month: null
      },

      lastUUID: {
        anthropicWorks: null,
        results: null
      }
    };
  },
  methods: {
    getPercentileI18NLabel,
    onClickShowAlertInitializeMap() {
      this.toastInitializeClose(); // Ensure it's false first

      this.$nextTick(() => {
        setTimeout(() => {
          this.showToast = true; // Open it again after a short delay
          this.$emit('onClickShowInitializeToast')
        }, 10); // Small delay ensures Vue detects the change
      });
    },
    isAccordionCurrent(value) {
      return this.accordion.current === value;
    },
    toastInitializeClose() {
      this.showToast = false;
    },
    accordionSetCurrent(value) {
      this.accordion.current = value;
      this.$emit('onClickAccordionSelect', this.accordion.current)
    },
    onClickAccordionToggle(value) {
      if (this.isAccordionCurrent(value) === false)
        this.accordion.current = value;
      else
        this.accordion.current = null;

      this.$emit('onClickAccordionSelect', this.accordion.current)
    },
    onClickReloadPage() {
      window.location.reload(); // This will refresh the entire page
    },
    onClickRunSimulation() {
      this.$emit('onClickRunSimulation');
    },
    onClickExplore() {
      this.$emit('onClickExplore', this.form);
    },
    editDate() {
      this.$emit("onEditInputClick", constants.STEPS.MONTH);
    },
    editPrecipitation() {
      this.$emit("onEditInputClick", constants.STEPS.PRECIPITATION_TEMP);
    },
    editTemperature() {
      this.$emit("onEditInputClick", constants.STEPS.PRECIPITATION_TEMP);
    },
    editSnowCondition() {
      this.$emit("onEditInputClick", constants.STEPS.SNOW);
    },
    editSoilWater() {
      this.$emit("onEditInputClick", constants.STEPS.SOIL_WATER);
    },
    createTooltip() {
      let tooltipClass = this.tooltipClassPoint;
      disposeTooltipsByClass(tooltipClass);
      setupTooltipsByClass(tooltipClass);

      let generalTooltipClass = '.tooltip-init'
      disposeTooltipsByClass(generalTooltipClass);
      setupTooltipsByClass(generalTooltipClass);
    },
    tooltipsShow() {
      this.createTooltip();
      let tooltipList = getTooltipsByClass(this.tooltipClassPoint);
      tooltipList.forEach(tooltip => {
        tooltip.show();
      });
      setTimeout(this.tooltipsHide, 5000);
    },
    tooltipsHide() {
      let tooltipList = getTooltipsByClass(this.tooltipClassPoint);
      tooltipList.forEach(tooltip => {
        tooltip.hide();
      })
    },
    tooltipGlossaryVariableKey() {
      return (this.variableIsSet) ? 'tooltips.GlossaryShowVariable' : 'tooltips.GlossaryShowVariablesAvailable';
    },
    tooltipGlossaryVariableName() {
      return this.variableIsSet ? this.propVariables[this.form.variable].getNameI18N(this.$i18n.locale) : '';
    },
    tooltipGlossaryOnClick() {
      this.$emit('tooltipGlossaryOnClick', this.form.variable);
    }
  },
  computed: {
    uuidSimulationResultsCurrent() {
      return UUIDfromList([this.propSimulationId, this.form.variable, this.form.month])
    },
    simulationFormComplete() {
      return this.form.month !== null && this.form.variable !== '';
    },
    simulationResultsEnabled() {
      return this.propUuidSimulationResults === null ||
          this.propUuidSimulationResults !== this.uuidSimulationResultsCurrent;
    },
    simulationMonthIsSummer() {
      return this.propSimulationInitialConditions !== null &&
          this.propSimulationInitialConditions.monthIsSummer === true
    },
    simulationEditDisabled() {
      return this.propLoadingRunSimulation === true || this.propLoadingExplore === true || this.propBlockAll === true;
    },
    tooltipClassPoint() {
      return `.tooltip-run-simulation`
    },
    variableIsSet() {
      return this.form.variable !== '';
    }
  },
  beforeUnmount() {
    this.tooltipsHide();
  },
  updated() {
    this.createTooltip();
  }
};
</script>
